import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { IsControl, IsChallenger } from 'Redux/ducks/split-test';
import SiteSlider from 'Components/Utility/SiteSlider/SiteSlider';
import BrowseBanner from 'Components/Search/BrowseBanner/BrowseBanner';
import imgixConfig from 'Config/imgix';
import store from 'Redux/store';
import Price from 'Components/Utility/Price/Price';
import pricing from 'Components/StaticSearch/games-under-pricing';
import BrowseButton from 'Components/Utility/BrowseButton';
import useViewport from 'Hooks/useViewport';

const TrackedLink = ({ to, children, ...props }) => {
  const onClick = () => {
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section: 'BrowseFanatical', value: to },
    });
  };

  return (
    <Link to={to} onClick={onClick} {...props}>
      {children}
    </Link>
  );
};

TrackedLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function QuickLinks() {
  const { formatMessage } = useIntl();

  const { isAboveMd, isBelowLg } = useViewport();

  const sliderSettings = {
    xs: {
      slidesToShow: 2,
      slidesToScroll: 2,
    },
    sm: {
      slidesToShow: 2,
      slidesToScroll: 2,
    },
    md: {
      slidesToShow: 3,
      slidesToScroll: 3,
    },
    lg: {
      slidesToShow: 4,
      slidesToScroll: 4,
    },
    xl: {
      slidesToShow: 4,
      slidesToScroll: 4,
    },
    xxl: {
      slidesToShow: 5,
      slidesToScroll: 5,
    },
  };

  const messages = {
    gameBundles: {
      id: 'home.quick.gameBundles',
      defaultMessage: 'Game Bundles',
    },
    onSale: {
      id: 'home.quick.onSale',
      defaultMessage: 'On Sale',
    },
    latestDeals: {
      id: 'home.quick.latestDeals',
      defaultMessage: 'Latest Deals',
    },
    topSellers: {
      id: 'home.quick.topSellers',
      defaultMessage: 'Top Sellers',
    },
    gamesUnderFive: {
      id: 'home.quick.gamesUnderFive',
      defaultMessage: 'Games Under £5',
    },
    vrGames: {
      id: 'home.quick.vrGames',
      defaultMessage: 'VR Games',
    },
    multiplayerGames: {
      id: 'home.quick.multiplayerGames',
      defaultMessage: 'Multiplayer Games',
    },
    lastChanceToSave: {
      id: 'home.quick.lastChanceToSave',
      defaultMessage: 'Last Chance to Save',
    },
    trendingDeals: {
      id: 'home.quick.trendingDeals',
      defaultMessage: 'Trending Deals',
    },
    bestGamesOf2025: {
      id: 'home.quick.bestGamesOf2025',
      defaultMessage: 'Best Games of 2025',
    },
    newReleases: {
      id: 'home.quick.newReleases',
      defaultMessage: 'New Releases',
    },
    macGames: {
      id: 'home.quick.macGames',
      defaultMessage: 'Mac Games',
    },
    steamDeckGames: {
      id: 'home.quick.steamDeckGames',
      defaultMessage: 'Steam Deck Games',
    },
    xboxGames: {
      id: 'home.quick.xboxGames',
      defaultMessage: 'Xbox Games',
    },
    giftCards: {
      id: 'home.quick.giftCards',
      defaultMessage: 'Gift Cards',
    },
  };

  return (
    <section className="quick-links container">
      {(isBelowLg || (isAboveMd && IsControl('MoreVisualBrowseBannersDesktop'))) &&
        <>
          <div className="section-heading-container">
            <h2>
              <FormattedMessage id="home.quick.browse" defaultMessage="Browse Fanatical" />
            </h2>
          </div>
          <div className="quicklinks-container">
            <div className="quick-link">
              <TrackedLink to="/bundle/games">
                <FormattedMessage id="home.quick.bundles" defaultMessage="Game Bundles" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/on-sale">
                <FormattedMessage id="home.quick.onsale" defaultMessage="On Sale Now" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/latest-deals">
                <FormattedMessage id="home.quick.latest" defaultMessage="Latest Deals" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/top-sellers">
                <FormattedMessage id="home.quick.topsellers" defaultMessage="Top Sellers" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/games-under/5">
                <FormattedMessage
                  id="home.quick.under"
                  defaultMessage="Games Under {price}"
                  tagName="span"
                  values={{
                    price: <Price
                      hideDecimals
                      price={pricing['5'].max}
                    />,
                  }}
                />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/vr-games">
                <FormattedMessage id="home.quick.vrgames" defaultMessage="VR Games" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/categories/rpg">
                <FormattedMessage id="home.quick.rpgames" defaultMessage="Role Playing Games" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/ending-soon">
                <FormattedMessage id="home.quick.lastchance" defaultMessage="Last Chance to Save" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/categories/anime">
                <FormattedMessage id="home.quick.animegames" defaultMessage="Anime Games" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/bundle/books">
                <FormattedMessage id="home.quick.books" defaultMessage="Books" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/categories/party">
                <FormattedMessage id="home.quick.fpsgames" defaultMessage="Party Games" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/trending-deals">
                <FormattedMessage id="home.quick.trendingdeals" defaultMessage="Trending Deals" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/new-releases">
                <FormattedMessage id="home.quick.newreleases" defaultMessage="New Releases" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/best-games-of-2025">
                <FormattedMessage id="home.quick.bestgamesof2025" defaultMessage="Best Games of 2025" />
              </TrackedLink>
            </div>
            <div className="quick-link">
              <TrackedLink to="/search">
                <FormattedMessage id="home.quick.browseall" defaultMessage="Browse All" />
              </TrackedLink>
            </div>
          </div>
        </>
      }

      {isAboveMd && IsChallenger('MoreVisualBrowseBannersDesktop') &&
        <>
          <div className="section-heading-container">
            <h2>
              <FormattedMessage id="home.quick.browse" defaultMessage="Browse Fanatical" />
            </h2>
            <BrowseButton className={'view-all-btn'} location="search" />
          </div>
          <div className="container p-0">
            <SiteSlider sliderSettings={sliderSettings}>
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.gameBundles)}
                pathname="/bundle/games"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/game-bundles.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.onSale)}
                pathname="/on-sale"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/on-sale.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.latestDeals)}
                pathname="/latest-deals"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/latest-deals-v2.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.gamesUnderFive)}
                pathname="/games-under/5"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/games-under-five.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.bestGamesOf2025)}
                pathname="/best-games-of-2025"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/best-of-2025.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.topSellers)}
                pathname="/top-sellers"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/top-sellers.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.steamDeckGames)}
                pathname="/steam-deck-games"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/steam-deck-games.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.lastChanceToSave)}
                pathname="/ending-soon"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/last-chance-to-save.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.multiplayerGames)}
                pathname="/categories/multiplayer"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/multiplayer-games.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.vrGames)}
                pathname="/vr-games"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/vr-games.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.trendingDeals)}
                pathname="/trending-deals"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/popular-games.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.newReleases)}
                pathname="/new-releases"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/new-releases.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.macGames)}
                pathname="/mac-games"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/mac-games.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.giftCards)}
                pathname="/gift-cards"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/gift-cards-v2.png?${imgixConfig}`}
              />
              <BrowseBanner
                squareDesign
                position="center"
                title={formatMessage(messages.xboxGames)}
                pathname="/publishers/xbox"
                backgroundImage={`https://cdn.fanatical.com/production/assets/banners/homepage/browse-fanatical/xbox-v2.png?${imgixConfig}`}
              />
            </SiteSlider>
          </div>
        </>
      }
    </section>
  );
}
